import React from 'react';
import { AI, LogoSmall, PencilEdit } from '@/resources/assets/images';
import ScheduledEmailHovering from '@/assets/images/scheduled-email-hover.png';
import RichEditor from '@/components/RichEditor/RichEditor';
import { sendAnalytics } from '@/services/analyticsService';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import { useSequencesContext } from '../SequencesContext';
import type { StepsType } from '../hooks/useSequences';

export default function SequencesStepsList({
	steps
}: {
	steps: StepsType[] | undefined;
}) {
	// border-l border-t border-b rounded-l-lg
	return (
		<div className="flex h-full w-full flex-col gap-2 overflow-y-auto border-dm-grey bg-dm-black3 p-4 lg:py-8 lg:pl-6 lg:pr-10">
			{steps?.map((step, index) => (
				<SequenceStepsItem
					key={step.id}
					index={index}
					step={step}
					editorBackground="dm-black2"
				/>
			))}
		</div>
	);
}

function SequenceStepsItem({
	step,
	index,
	editorBackground = 'dm-black3'
}: {
	step: StepsType;
	index: number;
	editorBackground?: string;
}) {
	const {
		setSequencePage,
		setSelectedTemplate,
		setSelectedStep,
		selectedSequence,
		sequencePage
	} = useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	function handleEditItem() {
		if (!selectedSequence) return;
		if (sequencePage === 'MY-SEQUENCES') {
			// We need to set the selected template to the current selected sequence if we are on the first page
			setSelectedTemplate({
				id: selectedSequence.id,
				name: selectedSequence.name,
				steps: selectedSequence.steps
			});
		}
		setSelectedStep(step);
		setSequencePage('STEPS-CREATION');

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: selectedSequence.id,
			eventName: 'user_clicked_edit_a_sequence'
		});
	}

	const stepOrder = index + 1;

	const itemTitle = `Send after ${step.sendAfterInterval} day${
		step.sendAfterInterval > 1 ? 's' : ''
	}`;

	return (
		<div className="flex flex-shrink-0 flex-col gap-2 border-b border-dm-grey pb-6 pt-4">
			<div className="flex flex-col gap-4 lg:gap-2">
				<div className="flex h-full min-h-[3.25rem] items-center justify-between px-4">
					<div className="flex flex-col gap-3 overflow-hidden lg:flex-row lg:items-center">
						<div className="flex h-8 w-[6.5rem] flex-shrink-0 items-center gap-3">
							<LogoSmall />
							<span className="whitespace-nowrap text-lg font-semibold text-dm-mid-grey">
								Step {stepOrder}
							</span>
						</div>
						<div className="flex flex-col gap-2">
							<div className="flex items-center gap-4 lg:whitespace-nowrap">
								<span className="flex items-center gap-2 text-sm font-semibold text-dm-gold lg:text-base">
									{stepOrder === 1 ? (
										<span>Send all</span>
									) : (
										<>
											<img
												src={ScheduledEmailHovering}
												alt="Activity Reports"
												className="h-4 w-4 lg:h-5 lg:w-5"
											/>
											{itemTitle}
										</>
									)}
								</span>
							</div>
							<span className="whitespace-normal font-semibold text-white lg:text-lg">
								{step.subject}
							</span>
						</div>
					</div>
					<div className="flex items-center self-start lg:ml-2">
						{step.body.includes('[ai_snippet]') && (
							<AI className="mr-2 h-6 w-6 text-dm-white" />
						)}
						<div className="my-auto flex h-10 items-center justify-center border-dark-grey pl-2 text-gray-200 lg:border-l lg:border-dashed">
							<button
								type="button"
								onClick={handleEditItem}
								className="h-9 w-9"
							>
								<PencilEdit />
							</button>
						</div>
					</div>
				</div>
			</div>
			<div className="border-b-2 border-dashed border-dm-grey" />
			<div className="h-40">
				<RichEditor
					onChange={() => {
						('');
					}}
					value={step.body}
					className={`h-full w-full bg-${editorBackground} !text-sm text-gray-200 lg:!text-base`}
					disabled
					disableDisabledStyles
					removeHoverStyle
				/>
			</div>
		</div>
	);
}
